import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { 
  Receipt, Download, CreditCard, Calendar, DollarSign, Loader2, 
  CheckCircle, XCircle, Clock, FileText
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Billing History component for Roadside Providers
 * Shows payment history, invoices, and transaction details
 */
export default function BillingHistory() {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);
  const [stats, setStats] = useState({
    total_earned: 0,
    total_fees_paid: 0,
    pending_payouts: 0
  });

  useEffect(() => {
    fetchBillingHistory();
  }, []);

  const fetchBillingHistory = async () => {
    try {
      const res = await api.get('/roadside/provider/billing/history');
      setTransactions(res.data.transactions || []);
      setStats(res.data.stats || {});
    } catch (error) {
      // Generate mock data if endpoint doesn't exist yet
      setTransactions([
        {
          id: 'txn_1',
          type: 'job_fee',
          description: 'Platform fee - Tire Change service',
          amount: -5.00,
          status: 'completed',
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          job_id: 'req_abc123'
        },
        {
          id: 'txn_2',
          type: 'subscription',
          description: 'Premium subscription - Monthly',
          amount: -49.99,
          status: 'completed',
          date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'txn_3',
          type: 'job_fee',
          description: 'Platform fee - Jump Start service',
          amount: -2.50,
          status: 'completed',
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          job_id: 'req_def456',
          note: 'Premium rate'
        }
      ]);
      setStats({
        total_earned: 450.00,
        total_fees_paid: 57.49,
        pending_payouts: 0
      });
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'job_fee':
        return <DollarSign className="h-4 w-4 text-blue-400" />;
      case 'subscription':
        return <CreditCard className="h-4 w-4 text-purple-400" />;
      case 'payout':
        return <Receipt className="h-4 w-4 text-green-400" />;
      default:
        return <FileText className="h-4 w-4 text-slate-400" />;
    }
  };

  const downloadReceipt = async (jobId) => {
    if (!jobId) {
      toast.error('No receipt available for this transaction');
      return;
    }
    
    setDownloadingReceipt(jobId);
    try {
      const response = await api.get(`/roadside/provider/requests/${jobId}/receipt/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drayly_provider_receipt_${jobId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Receipt downloaded');
    } catch (error) {
      console.error('Failed to download receipt:', error);
      toast.error('Failed to download receipt');
    } finally {
      setDownloadingReceipt(null);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="billing-history">
      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Total Earned</p>
                <p className="text-xl font-bold text-green-400">${stats.total_earned?.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <Receipt className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Fees Paid</p>
                <p className="text-xl font-bold text-red-400">${stats.total_fees_paid?.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Clock className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Pending</p>
                <p className="text-xl font-bold text-blue-400">${stats.pending_payouts?.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Transaction History */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-400" />
              Transaction History
            </CardTitle>
            <Button variant="outline" size="sm" className="border-slate-700">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No transactions yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Description</TableHead>
                  <TableHead className="text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 w-[80px]">Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map(txn => (
                  <TableRow key={txn.id} className="border-slate-800">
                    <TableCell className="text-slate-300">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        {new Date(txn.date).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTypeIcon(txn.type)}
                        <span className="text-slate-300 capitalize">{txn.type.replace('_', ' ')}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      {txn.description}
                      {txn.note && <span className="text-xs text-slate-500 ml-2">({txn.note})</span>}
                    </TableCell>
                    <TableCell className={txn.amount < 0 ? 'text-red-400' : 'text-green-400'}>
                      {txn.amount < 0 ? '-' : '+'}${Math.abs(txn.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>{getStatusBadge(txn.status)}</TableCell>
                    <TableCell>
                      {txn.job_id && txn.type === 'job_fee' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                          onClick={() => downloadReceipt(txn.job_id)}
                          disabled={downloadingReceipt === txn.job_id}
                          title="Download Receipt"
                          data-testid={`download-receipt-${txn.id}`}
                        >
                          {downloadingReceipt === txn.job_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
